import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getSlots, updateSlot, bulkUpdateSlots } from '../api/skipzyAdmin';

// Order Ops → Slots Manage.
//
// One section per day, five 2-hour slot cards in it. Each card is a load meter:
// the big number is how many places are left, the bar fills as the slot books,
// and the colour says where it stands (open → filling up → full, or blocked).
// Capacity edits save themselves a moment after the last tap. The numbers live
// in the Skipzy backend and are what the app and the website show customers as
// "slots left", so a change is live on their next fetch.

const LOW_LEFT = 3; // at or below this many left a slot reads "filling up"
const SAVE_DELAY_MS = 700;
const PRESETS = [
  { days: 3, label: 'Next 3 days' },
  { days: 7, label: 'Next 7 days' },
  { days: 14, label: 'Next 14 days' },
];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// "Today", "Tomorrow", then the weekday — with the date beside it.
function dayHeading(iso) {
  const d = new Date(`${iso}T00:00:00`);
  const today = isoDate(new Date());
  const tomorrow = isoDate(addDays(new Date(), 1));
  const date = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  if (iso === today) return { name: 'Today', date };
  if (iso === tomorrow) return { name: 'Tomorrow', date };
  return { name: d.toLocaleDateString('en-IN', { weekday: 'long' }), date: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) };
}

function errText(e, fallback) {
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
}

// A slot more than 30 minutes past its start is closed to customers (2-4 PM takes
// bookings until 2:30), so there is nothing useful to edit on it.
function hasStarted(slot) {
  const start = new Date(`${slot.slot_date}T00:00:00`);
  start.setHours(slot.start_hour, 0, 0, 0);
  return Date.now() - start.getTime() >= 30 * 60 * 1000;
}

function statusOf(slot) {
  if (slot.is_blocked) return 'blocked';
  if (slot.available <= 0) return 'full';
  if (slot.available <= LOW_LEFT) return 'low';
  return 'open';
}

const STATUS = {
  open: { label: 'Open', bar: 'bg-zoddle-green', pill: 'bg-zoddle-green/15 text-emerald-800', num: 'text-gray-900' },
  low: { label: 'Filling up', bar: 'bg-amber-400', pill: 'bg-amber-100 text-amber-800', num: 'text-amber-700' },
  full: { label: 'Full', bar: 'bg-zoddle-pink', pill: 'bg-rose-100 text-rose-700', num: 'text-rose-600' },
  blocked: { label: 'Blocked', bar: 'bg-gray-300', pill: 'bg-gray-200 text-gray-600', num: 'text-gray-400' },
};

const HATCH = {
  backgroundImage:
    'repeating-linear-gradient(135deg, rgba(0,0,0,0.045) 0 6px, transparent 6px 12px)',
};

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green focus-visible:ring-offset-1';

function SlotCard({ slot, onSave }) {
  const [cap, setCap] = useState(slot.capacity);
  const [state, setState] = useState('idle'); // idle | pending | saving | saved
  const timer = useRef(null);
  const savedTimer = useRef(null);
  const capRef = useRef(cap);
  capRef.current = cap;

  // Take the server's number back when it changes underneath us (refresh, bulk edit).
  useEffect(() => { setCap(slot.capacity); }, [slot.capacity]);
  useEffect(() => () => { clearTimeout(timer.current); clearTimeout(savedTimer.current); }, []);

  const started = hasStarted(slot);
  const blocked = slot.is_blocked;
  const status = statusOf({ ...slot, available: Math.max(cap - slot.booked_count, 0) });
  const look = STATUS[status];
  const left = Math.max(cap - slot.booked_count, 0);
  const over = Math.max(slot.booked_count - cap, 0);
  const fill = cap > 0 ? Math.min(slot.booked_count / cap, 1) : slot.booked_count > 0 ? 1 : 0;

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (capRef.current === slot.capacity) { setState('idle'); return; }
    setState('saving');
    const ok = await onSave(slot, { capacity: capRef.current });
    if (ok) {
      setState('saved');
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setState('idle'), 1600);
    } else {
      setState('idle');
    }
  }, [slot, onSave]);

  function change(next) {
    const n = Math.max(0, Math.min(500, Number.isFinite(next) ? Math.round(next) : 0));
    setCap(n);
    setState('pending');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY_MS);
  }

  async function toggleBlock() {
    await onSave(slot, { is_blocked: !blocked });
  }

  const dim = started ? 'opacity-55' : '';
  const stepBtn = `flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-sm font-bold leading-none text-gray-600 transition hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing}`;
  const note =
    state === 'pending' || state === 'saving' ? 'Saving…'
    : state === 'saved' ? 'Saved'
    : null;

  return (
    <div
      className={`flex flex-col rounded-xl border bg-white px-2.5 py-2 transition-colors ${
        blocked ? 'border-gray-200 bg-gray-50' : status === 'full' ? 'border-rose-200' : 'border-gray-200'
      } ${dim}`}
      style={blocked ? HATCH : undefined}
    >
      {/* when + where it stands */}
      <div className="flex items-center justify-between gap-1.5">
        <p className="text-[13px] font-bold leading-tight text-gray-900">{slot.display}</p>
        <span className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold ${started ? 'bg-gray-100 text-gray-500' : look.pill}`}>
          {started ? 'Started' : look.label}
        </span>
      </div>

      {/* the number that matters, with the booked count beside it */}
      <div className="mt-1.5 flex items-baseline justify-between gap-2">
        <p className="flex items-baseline gap-1">
          <span className={`text-2xl font-extrabold leading-none tabular-nums ${blocked ? 'text-gray-400' : look.num}`}>
            {blocked ? '–' : left}
          </span>
          <span className="text-xs text-gray-500">{blocked ? 'blocked' : 'left'}</span>
        </p>
        <p className="text-[11px] tabular-nums text-gray-500">
          <span className="font-semibold text-gray-700">{slot.booked_count}</span>/{cap} booked
          {over > 0 && (
            <span
              className="ml-1 font-semibold text-rose-600"
              title="Over capacity. Nobody is cancelled; no new bookings until places free up."
            >
              +{over} over
            </span>
          )}
        </p>
      </div>

      {/* load meter */}
      <div
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="img"
        aria-label={`${slot.booked_count} of ${cap} booked`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none ${look.bar}`}
          style={{ width: `${Math.round(fill * 100)}%` }}
        />
      </div>

      {/* controls */}
      <div className="mt-2 flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1" role="group" aria-label={`Capacity for ${slot.display}`}>
          <button type="button" onClick={() => change(cap - 1)} disabled={started || cap <= 0} aria-label="Decrease capacity" className={stepBtn}>
            −
          </button>
          <input
            type="number"
            min="0"
            max="500"
            inputMode="numeric"
            value={cap}
            disabled={started}
            onChange={(e) => change(e.target.value === '' ? 0 : Number(e.target.value))}
            onBlur={flush}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            aria-label="Capacity"
            className={`h-6 w-9 rounded-md border border-gray-200 bg-white text-center text-xs font-bold tabular-nums text-gray-900 [appearance:textfield] disabled:bg-gray-50 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${focusRing}`}
          />
          <button type="button" onClick={() => change(cap + 1)} disabled={started || cap >= 500} aria-label="Increase capacity" className={stepBtn}>
            +
          </button>
          <span className="ml-0.5 w-11 text-[10px] text-emerald-700" aria-live="polite">{note}</span>
        </div>

        {/* accepting bookings switch */}
        <button
          type="button"
          role="switch"
          aria-checked={!blocked}
          aria-label={blocked ? 'Start taking bookings' : 'Stop taking bookings'}
          title={blocked ? 'Blocked. Tap to start taking bookings' : 'Taking bookings. Tap to block'}
          onClick={toggleBlock}
          disabled={started}
          className={`relative h-5 w-8 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            blocked ? 'bg-gray-300' : 'bg-zoddle-green'
          } ${focusRing}`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left] motion-reduce:transition-none ${
              blocked ? 'left-0.5' : 'left-[0.9rem]'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function DaySection({ date, slots, onSave }) {
  const heading = dayHeading(date);
  const open = slots.filter((s) => !s.is_blocked);
  const booked = open.reduce((n, s) => n + s.booked_count, 0);
  const total = open.reduce((n, s) => n + s.capacity, 0);
  const pct = total > 0 ? Math.min(booked / total, 1) : 0;

  return (
    <section aria-label={`${heading.name} ${heading.date}`}>
      <div className="mb-2 flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
        <h3 className="text-base font-extrabold text-gray-900">
          {heading.name}
          <span className="ml-2 text-sm font-medium text-gray-400">{heading.date}</span>
        </h3>
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-28 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
            <div className="h-full rounded-full bg-gray-800" style={{ width: `${Math.round(pct * 100)}%` }} />
          </div>
          <p className="text-xs tabular-nums text-gray-500">
            <span className="font-semibold text-gray-800">{booked}</span> of {total} booked across the day
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
        {slots.map((s) => <SlotCard key={s.id} slot={s} onSave={onSave} />)}
      </div>
    </section>
  );
}

function Skeleton() {
  return (
    <div className="space-y-8" aria-hidden="true">
      {[0, 1, 2].map((d) => (
        <div key={d}>
          <div className="mb-3 h-6 w-40 animate-pulse rounded bg-gray-100" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SlotsManageTab() {
  const [days, setDays] = useState(3); // today + the next two
  const [custom, setCustom] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => isoDate(new Date()));
  const [dateTo, setDateTo] = useState(() => isoDate(addDays(new Date(), 2)));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCap, setBulkCap] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setSlots(await getSlots(dateFrom, dateTo)); }
    catch (e) { setError(errText(e, 'Could not load slots. Check your connection and refresh.')); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  function pickPreset(n) {
    setCustom(false);
    setDays(n);
    setDateFrom(isoDate(new Date()));
    setDateTo(isoDate(addDays(new Date(), n - 1)));
  }

  const byDay = useMemo(() => {
    const m = new Map();
    slots.forEach((s) => {
      if (!m.has(s.slot_date)) m.set(s.slot_date, []);
      m.get(s.slot_date).push(s);
    });
    return [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, list]) => [date, list.sort((a, b) => a.start_hour - b.start_hour)]);
  }, [slots]);

  // Resolves true when saved, so a card can show "Saved" — failures roll back to the server's numbers.
  const save = useCallback(async (slot, patch) => {
    setNotice('');
    try {
      const updated = await updateSlot(slot.id, patch);
      setSlots((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      return true;
    } catch (e) {
      setNotice(errText(e, 'That change did not save.'));
      load();
      return false;
    }
  }, [load]);

  async function applyBulk() {
    const n = Number(bulkCap);
    if (bulkCap === '' || !Number.isInteger(n) || n < 0 || n > 500) { setNotice('Enter a whole number from 0 to 500.'); return; }
    if (!window.confirm(`Set every slot from ${dateFrom} to ${dateTo} to a capacity of ${n}?`)) return;
    setBulkBusy(true); setNotice('');
    try {
      await bulkUpdateSlots({ date_from: dateFrom, date_to: dateTo, capacity: n });
      setBulkCap(''); setBulkOpen(false);
      await load();
    } catch (e) {
      setNotice(errText(e, 'Could not apply that to every slot.'));
    } finally { setBulkBusy(false); }
  }

  return (
    <div>
      <p className="mb-4 max-w-2xl text-sm text-gray-500">
        Places per 2-hour slot. Customers see what is left in the app and on the website, and a full slot can&apos;t be booked.
      </p>

      {/* range + bulk */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-xl bg-gray-100 p-1" role="group" aria-label="Date range">
          {PRESETS.map((p) => {
            const on = !custom && days === p.days;
            return (
              <button
                key={p.days}
                type="button"
                onClick={() => pickPreset(p.days)}
                aria-pressed={on}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${on ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'} ${focusRing}`}
              >
                {p.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setCustom((c) => !c)}
            aria-pressed={custom}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${custom ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'} ${focusRing}`}
          >
            Pick dates
          </button>
        </div>

        {custom && (
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date"
              className={`rounded-lg border border-gray-200 px-3 py-1.5 text-sm ${focusRing}`} />
            <span className="text-sm text-gray-400">to</span>
            <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} aria-label="To date"
              className={`rounded-lg border border-gray-200 px-3 py-1.5 text-sm ${focusRing}`} />
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={load}
            className={`rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 ${focusRing}`}>
            Refresh
          </button>
          <button type="button" onClick={() => setBulkOpen((o) => !o)} aria-expanded={bulkOpen}
            className={`rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 ${focusRing}`}>
            Set all slots
          </button>
        </div>
      </div>

      {bulkOpen && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <label htmlFor="bulk-cap" className="text-sm text-gray-700">
            Set every slot shown to a capacity of
          </label>
          <input id="bulk-cap" type="number" min="0" max="500" value={bulkCap} onChange={(e) => setBulkCap(e.target.value)}
            className={`h-9 w-20 rounded-lg border border-gray-200 bg-white text-center text-sm font-bold tabular-nums ${focusRing}`} />
          <button type="button" onClick={applyBulk} disabled={bulkBusy || bulkCap === ''}
            className={`rounded-lg bg-zoddle-green px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 ${focusRing}`}>
            {bulkBusy ? 'Applying…' : 'Apply to all'}
          </button>
        </div>
      )}

      {notice && (
        <div role="alert" className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
          {notice}
        </div>
      )}
      {error && (
        <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
          <span>{error}</span>
          <button type="button" onClick={load} className="font-semibold underline">Try again</button>
        </div>
      )}

      {loading && slots.length === 0 ? <Skeleton />
        : byDay.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-400">No slots for these dates. Pick another range.</p>
        ) : (
          <div className="space-y-6">
            {byDay.map(([date, list]) => <DaySection key={date} date={date} slots={list} onSave={save} />)}
          </div>
        )}
    </div>
  );
}
